import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { formatUtcDate } from '../../shared/date-utils';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ModernizationReadinessService } from '../../services/modernization-readiness.service';
import { ModernizationReadinessJob, ModernizationReadinessJobLog, ModernizationReadinessJobStatus } from '../../models/chat.models';
import { JsonViewDialogComponent } from '../../shared/json-view-dialog/json-view-dialog.component';

import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../material.module';
import { LeftMenuComponent } from '../../menu/left-menu/left-menu.component';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { JobLogsSkeletonComponent } from './job-logs-skeleton/job-logs-skeleton.component';
import { JobLogsTableSkeletonComponent } from './job-logs-table-skeleton/job-logs-table-skeleton.component';

@Component({
  selector: 'app-job-logs',
  templateUrl: './job-logs.component.html',
  styleUrls: ['./job-logs.component.scss'],
  imports: [FormsModule, MaterialModule, LeftMenuComponent, SpinnerComponent, JobLogsSkeletonComponent, JobLogsTableSkeletonComponent]
})
export class JobLogsComponent implements OnInit, OnDestroy {
  jobId: number = 0;
  job: ModernizationReadinessJob | null = null;
  jobLogs: ModernizationReadinessJobLog[] = [];
  jobLogsTotal = 0;
  jobLogsPageNumber = 1;
  jobLogsPageSize = 50;
  pageLoading = false;
  
  loading = false;
  error: string | null = null;
  
  // Filters
  filterApplicationName: string = '';
  filterResult: string = ''; // 'success', 'failed', 'skipped', or ''
  
  // Debounce subject for text filter
  private filterTextSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private initialPageRequestsPending = 0;
  
  // Math for template
  Math = Math;
  
  // Enum for template
  ModernizationReadinessJobStatus = ModernizationReadinessJobStatus;
  
  // Display columns
  jobLogsColumns: string[] = ['applicationName', 'outcome', 'success', 'readinessScore', 'recommendation', 'processingTime', 'error'];
  
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private modernizationReadinessService: ModernizationReadinessService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.jobId = +params['id'];
      if (this.jobId) {
        this.beginInitialPageLoad(2);
        this.loadJobDetails(true);
        this.loadJobLogs(this.jobId, 1, true);
      }
    });
    
    // Setup debounced filter for text input
    this.filterTextSubject.pipe(
      debounceTime(1000),
      distinctUntilChanged()
    ).subscribe(() => {
      this.applyFilters();
    });
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.filterTextSubject.complete();
  }

  private beginInitialPageLoad(requests: number): void {
    this.initialPageRequestsPending = requests;
    this.pageLoading = true;
    this.error = null;
  }

  private completeInitialPageLoad(): void {
    this.initialPageRequestsPending = Math.max(0, this.initialPageRequestsPending - 1);
    if (this.initialPageRequestsPending === 0) {
      this.pageLoading = false;
    }
  }

  loadJobDetails(isInitialLoad = false): void {
    this.modernizationReadinessService.getJobById(this.jobId).subscribe({
      next: (job) => {
        this.job = job;
        if (isInitialLoad) {
          this.completeInitialPageLoad();
        }
      },
      error: (err) => {
        console.error('Error loading job details:', err);
        this.error = 'Failed to load job details';
        if (isInitialLoad) {
          this.completeInitialPageLoad();
        }
      }
    });
  }

  loadJobLogs(jobId: number, pageNumber: number, isInitialLoad = false): void {
    this.jobLogsPageNumber = pageNumber;
    if (!isInitialLoad) {
      this.loading = true;
    }
    
    this.modernizationReadinessService.getJobLogs(
      jobId, 
      pageNumber, 
      this.jobLogsPageSize,
      this.filterApplicationName || undefined,
      this.filterResult || undefined
    ).subscribe({
      next: (response) => {
        this.jobLogs = response.logs || [];
        this.jobLogsTotal = response.totalCount || 0;
        if (isInitialLoad) {
          this.completeInitialPageLoad();
        } else {
          this.loading = false;
        }
      },
      error: (err) => {
        console.error('Error loading job logs:', err);
        this.error = 'Failed to load job logs';
        if (isInitialLoad) {
          this.completeInitialPageLoad();
        } else {
          this.loading = false;
        }
      }
    });
  }

  goBack = (): void => {
    this.router.navigate(['/admin/ai-cmdb-assessment'], { queryParams: { tab: 2 } });
  }

  viewJsonDialog = (log: ModernizationReadinessJobLog): void => {
    if (!log.aiResponseJson) return;
    
    this.dialog.open(JsonViewDialogComponent, {
      width: '800px',
      maxHeight: '90vh',
      data: {
        title: `AI Response for ${log.applicationName || log.businessApplicationSysId}`,
        json: log.aiResponseJson
      }
    });
  }

  onPageChange(event: PageEvent): void {
    this.jobLogsPageSize = event.pageSize;
    this.jobLogsPageNumber = event.pageIndex + 1;
    this.loadJobLogs(this.jobId, this.jobLogsPageNumber);
  }

  getTotalPages(): number {
    return Math.ceil(this.jobLogsTotal / this.jobLogsPageSize);
  }

  onFilterTextChange(): void {
    this.filterTextSubject.next(this.filterApplicationName);
  }

  applyFilters(): void {
    this.jobLogsPageNumber = 1;
    this.loadJobLogs(this.jobId, 1);
  }

  clearFilters = (): void => {
    this.filterApplicationName = '';
    this.filterResult = '';
    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    return this.filterApplicationName !== '' || this.filterResult !== '';
  }

  // ==================== Helper Methods ====================

  isStatusRunning(status: ModernizationReadinessJobStatus | string): boolean {
    return status === 'Running' || status === ModernizationReadinessJobStatus.Running;
  }

  isStatusCompleted(status: ModernizationReadinessJobStatus | string): boolean {
    return status === 'Completed' || status === ModernizationReadinessJobStatus.Completed;
  }

  isStatusFailed(status: ModernizationReadinessJobStatus | string): boolean {
    return status === 'Failed' || status === ModernizationReadinessJobStatus.Failed;
  }

  getStatusText(status: ModernizationReadinessJobStatus | string): string {
    if (typeof status === 'string') {
      return status;
    }
    return ModernizationReadinessJobStatus[status] || 'Unknown';
  }

  getStatusBadgeClass(status: ModernizationReadinessJobStatus | string): string {
    if (this.isStatusRunning(status)) {
      return 'badge-primary';
    } else if (this.isStatusCompleted(status)) {
      return 'badge-success';
    } else if (this.isStatusFailed(status)) {
      return 'badge-danger';
    }
    return 'badge-secondary';
  }

  getOutcomeFromJson(log: ModernizationReadinessJobLog): string {
    if (!log.aiResponseJson) return '';
    try {
      const r = JSON.parse(log.aiResponseJson);
      return r.outcome ?? r.Outcome ?? '';
    } catch { return ''; }
  }

  getOutcomeBadgeClass(outcome: string): string {
    switch (outcome.toLowerCase()) {
      case 'excluded':       return 'badge-secondary';
      case 'decommission':   return 'badge-danger';
      case 'modernize':      return 'badge-warning';
      case 'already modern': return 'badge-success';
      default:               return 'badge-info';
    }
  }

  getScoreBadgeClass(score: number | null | undefined): string {
    if (score === null || score === undefined) return 'badge-secondary';
    if (score >= 80) return 'badge-success';
    if (score >= 60) return 'badge-info';
    if (score >= 40) return 'badge-warning';
    return 'badge-danger';
  }

  getRecommendationBadgeClass(recommendation: string | null | undefined): string {
    if (!recommendation) return 'badge-secondary';
    const rec = recommendation.toLowerCase();
    if (rec.includes('retire')) return 'badge-danger';
    if (rec.includes('rehost')) return 'badge-success';
    if (rec.includes('replatform')) return 'badge-info';
    if (rec.includes('refactor') || rec.includes('rearchitect')) return 'badge-warning';
    return 'badge-secondary';
  }

  formatDuration(job: ModernizationReadinessJob | null): string {
    if (!job) return '-';
    
    if (!job.startedAt) {
      return 'Not started';
    }
    
    const end = job.completedAt ? new Date(job.completedAt) : new Date();
    const start = new Date(job.startedAt);
    const diffMs = end.getTime() - start.getTime();
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  formatDate(date: Date | null | undefined): string {
    if (!date) return '-';
    return formatUtcDate(new Date(date), 'short');
  }
}
